import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Button,
  Select,
  Flex,
  Stack,
  Box,
  HStack,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import Edit_Conductor from "./Edit_Conductor";
import Edit_driver from "./Edit_driver";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const StaffList = () => {
  const [staff, setStaff] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [filter, setFilter] = useState("Driver"); // Filter for Conductor or Driver
  const [isModalOpen, setIsModalOpen] = useState(false); // To control the modal visibility
  const [selectedStaffId, setSelectedStaffId] = useState(null); // Store selected staff ID for deletion
  const toast = useToast();

  const fetchStaff = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/bus/staff/list_all_bus_staff.php`,
        { params: { school_id: sessionStorage.getItem("school_id") } }
      );

      if (response.data.success) {
        // Filter data based on the selected filter
        const filteredStaff = response.data.data.filter(
          (staff) => staff.staff_type === filter
        );
        setStaff(filteredStaff);
      } else {
        console.error("Failed to fetch staff data");
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching the staff data.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [filter]);

  const handleDelete = async () => {
    const userId = sessionStorage.getItem("user_id");
    const schoolId = sessionStorage.getItem("school_id");

    if (!userId || !schoolId) {
      toast({
        title: "Error",
        description: "User or school ID is missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/bus/staff/delete_bus_staff.php`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            user_id: userId,
            school_id: schoolId,
            id: selectedStaffId,
          },
        }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: `${filter} deleted successfully!`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchStaff();
      } else {
        toast({
          title: "Error",
          description: `Failed to delete ${filter}: ${response.data.message}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the staff member.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsModalOpen(false); // Close the modal after the action
    }
  };

  const handlePageChange = (page, totalPages) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const renderTable = (data) => {
    const totalPages = Math.ceil(data.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const currentData = data.slice(startIndex, startIndex + entriesPerPage);

    return (
      <>
        <TableContainer>
          <Table
            sx={{
              "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              "th, td": {
                textAlign: "center",
              },
            }}
          >
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Phone Number</Th>
                {filter === "Driver" && <Th>Driver License No</Th>}
                <Th>Address</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentData.map((item, index) => (
                <Tr key={index}>
                  <Td>{item.name}</Td>
                  <Td>{item.phone_no}</Td>
                  {filter === "Driver" && <Td>{item.driving_license_no}</Td>}
                  <Td>{item.address}</Td>
                  <Td>
                    <Badge
                      borderRadius="full"
                      fontSize="10px"
                      colorScheme={item.status === 1 ? "green" : "red"}
                    >
                      {item.status === 1 ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td>
                    {filter === "Conductor" ? (
                      <Edit_Conductor
                        RefreshData={fetchStaff}
                        staffId={item.id}
                      />
                    ) : (
                      <Edit_driver RefreshData={fetchStaff} staffId={item.id} />
                    )}
                    <IconButton
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => {
                        setSelectedStaffId(item.id);
                        setIsModalOpen(true); // Open modal when delete is clicked
                      }}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex justify="end" align="center" p={3} className="sb2">
          <Button
            onClick={() => handlePageChange(currentPage - 1, totalPages)}
            isDisabled={currentPage === 1}
          >
            Prev
          </Button>

          <Stack direction="row" spacing={2} mx={2}>
            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i}
                onClick={() => handlePageChange(i + 1, totalPages)}
                colorScheme={currentPage === i + 1 ? "teal" : "gray"}
              >
                {i + 1}
              </Button>
            ))}
          </Stack>

          <Button
            onClick={() => handlePageChange(currentPage + 1, totalPages)}
            isDisabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Flex>
      </>
    );
  };

  return (
    <Box>
      <HStack justify="space-between" className="sb" p={3}>
        <Select
          width="120px"
          value={entriesPerPage}
          onChange={(e) => {
            setEntriesPerPage(Number(e.target.value));
            setCurrentPage(1); // Reset to first page when entries per page changes
          }}
        >
          <option value="10">10</option>
          <option value="50">50</option>
          <option value="70">70</option>
        </Select>

        <Select
          width="150px"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="Driver">Driver</option>
          <option value="Conductor">Conductor</option>
        </Select>
      </HStack>
      {staff.length > 0 ? renderTable(staff) : <Box>No {filter}s found.</Box>}

      {/* Modal for deletion confirmation */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this staff member?
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={handleDelete} mr={3}>
              Delete
            </Button>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StaffList;
